import { useTranslation } from 'react-i18next';
import {
  CS_CHAT_STATE_UNKNOWN,
  isCsChatFilterState,
  type CsChatFilterState,
} from '@cortex/domain-ac-mens-hub-api';

/** @graph-connects none */
const PILL = 'inline-block rounded-full px-2 py-0.5 text-xs font-medium';

/**
 * 取り込みの問題を表す状態の札。実在の状態と混ざらないよう点線の枠にする。
 * @graph-connects none
 */
const DASHED = 'inline-block rounded-full border border-dashed px-[7px] py-px text-xs font-medium';

/**
 * チャット状態ごとの表示ラベル（i18n キー）と札の見た目。
 * 一覧の札・詳細パネルの札・絞り込みの選択肢が、すべてこの定義を使う。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business チャット状態ごとの表示ラベルと札の見た目を定める。未取得・不明は点線枠の札にする
 * @graph-connects none
 */
export const CS_CHAT_STATE_DISPLAY: Record<
  CsChatFilterState,
  { labelKey: string; className: string }
> = {
  opened: {
    labelKey: 'csChats.stateOpened',
    className: `${PILL} bg-[var(--color-accent-primary)]/10 text-[var(--color-accent-primary)]`,
  },
  queued: {
    labelKey: 'csChats.stateQueued',
    className: `${PILL} bg-[var(--color-info)]/10 text-[var(--color-info)]`,
  },
  snoozed: {
    labelKey: 'csChats.stateSnoozed',
    className: `${PILL} bg-[var(--color-warning)]/10 text-[var(--color-warning)]`,
  },
  initial: {
    labelKey: 'csChats.stateInitial',
    className: `${PILL} bg-[var(--color-text-muted)]/10 text-[var(--color-text-muted)]`,
  },
  missed: {
    labelKey: 'csChats.stateMissed',
    className: `${PILL} bg-[var(--color-error)]/10 text-[var(--color-error)]`,
  },
  closed: {
    labelKey: 'csChats.stateClosed',
    className: `${PILL} bg-[var(--color-success)]/10 text-[var(--color-success)]`,
  },
  unfetched: {
    labelKey: 'csChats.stateUnfetched',
    className: `${DASHED} border-[var(--color-warning)] text-[var(--color-warning)]`,
  },
  unknown: {
    labelKey: 'csChats.stateUnknown',
    className: `${DASHED} border-[var(--color-text-muted)] text-[var(--color-text-muted)]`,
  },
};

/**
 * 状態値から表示定義を引く。定義に無い値（廃止された assigned など）は「不明」として扱う。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 状態値から表示定義を引く。既知の状態以外の値は「不明」として扱う
 * @graph-connects none
 */
export function resolveCsChatStateDisplay(state: string): { labelKey: string; className: string } {
  return isCsChatFilterState(state)
    ? CS_CHAT_STATE_DISPLAY[state]
    : CS_CHAT_STATE_DISPLAY[CS_CHAT_STATE_UNKNOWN];
}

/**
 * チャット状態の札。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business チャット状態を色付きの札で表示する。未取得・不明は点線枠で出す
 * @graph-connects resolveCsChatStateDisplay [calls] 状態の表示定義
 */
export function CsChatStateBadge({ state }: { state: string }) {
  const { t } = useTranslation();
  const display = resolveCsChatStateDisplay(state);
  return <span className={display.className}>{t(display.labelKey)}</span>;
}
